import { useEffect, useRef, useState, useMemo, forwardRef, useImperativeHandle } from "react";
import { Input } from "components/input/input.tsx";
import { Edit } from "assets/icons";
import { ResourceType } from "shared/types/resource";
import {
  Button,
  Spinner,
} from "@heroui/react";
import { useNavigate } from "react-router-dom";
import { debounce } from "lodash";
import ButtonNew from "components/buttonNew/buttonNew";
import {
  Buildings,
  MagnifyingGlass,
  User,
  UsersThree,
  HardHat,
  ChatCircle,
  CurrencyEur,
  Wrench,
} from "@phosphor-icons/react";
import * as Sentry from "@sentry/react";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { useSession } from "@clerk/clerk-react";
import { API_URL } from "constants/index";
import { AddressPicker } from "components/index";
import { fetchResourcesFromAPI } from "shared/api/fetch-resources";
import { toast } from "react-toastify";

export enum SearchMode {
  SEARCH_AND_REDIRECT, // when this mode is selected it displays the search bar by default (+ redirects when clicking depending on the logic passed in by onSelect)
  SEARCH_AND_PRESENT, // when this mode is selected it display the search bar only when in editMode and a clickable link otherwise to the default selected resource
  JUST_PRESENT, // just presents the resource and linking
}

interface DirectResource {
  id: number;
  resourceId: string;
  type: string;
  name: string;
  metadata1?: string;
  metadata2?: string;
  outstandingDebt?: number;
  sharePointFolderId?: string;
  sharePointFolderUrl?: string;
  sharePointPicturesFolderId?: string;
  sharePointPicturesFolderUrl?: string;
  // Additional fields for renovation projects
  year?: number;
  budget?: number;
  invoicedAmount?: number;
  responsibleUserId?: string;
  responsibleUser?: {
    id: string;
    first_name: string;
    last_name: string;
    image_url?: string;
  };
  status?: string;
  buildingId?: string;
}

export interface DirectSearchComponentRef {
  focusInput: () => void;
}

interface DirectSearchComponentProps {
  resourceFilter?: ResourceType[];
  onSelect?: (
    resource:
      | {
          id: number;
          resourceId: string | any;
          type: ResourceType;
          name: string;
        }
      | any,
  ) => void;
  label?: string;
  isCleared?: () => void;
  initialValue?: string | number;
  initialValueType?: string;
  metadataFilter?: string;
  initialResource?: Partial<DirectResource> | null;
  api?: any;
  mode?: SearchMode;
  hasEditToggle?: boolean;
  editMode?: boolean;
  onMobileNavSelect?: any;
  customData?: any[] | undefined;
  classNames?: string;
  excludeRenovationProjects?: boolean;
}

type QuickCreateFormData = {
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  companyName?: string;
  address?: {
    formattedAddress: string;
    placeId: string;
  };
};

type QuickCreateMode = 'none' | 'contact' | 'company';

// Cache for all resources - shared across all instances
let resourcesCache: DirectResource[] | null = null;
let cachePromise: Promise<DirectResource[]> | null = null;

// Cache for renovation projects - shared across all instances
let renovationProjectsCache: DirectResource[] | null = null;
let renovationProjectsCachePromise: Promise<DirectResource[]> | null = null;

// Cache for building contact IDs - keys are "contact-{id}" or "company-{id}"
let buildingContactIdsCache: Set<string> | null = null;
// Cache for building contacts grouped by buildingId
let buildingContactsByBuildingCache: Record<string, { entityId: number; name: string; type: 'contact' | 'company'; reason: string }[]> | null = null;
let buildingContactIdsCachePromise: Promise<void> | null = null;


const DirectSearchComponent = forwardRef<DirectSearchComponentRef, DirectSearchComponentProps>(({
  label,
  onSelect,
  resourceFilter = [],
  initialValue = "",
  initialResource,
  initialValueType = "object",
  metadataFilter,
  mode = SearchMode.SEARCH_AND_REDIRECT,
  hasEditToggle = false,
  isCleared,
  editMode,
  api,
  onMobileNavSelect,
  customData,
  classNames = ``,
  excludeRenovationProjects = false,
}, ref) => {
  const [searchTerm, setSearchTerm] = useState(initialValue);
  const [searchResults, setSearchResults] = useState<DirectResource[]>([]);
  const [showResults, setShowResults] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isEditMode, setIsEditMode] = useState(editMode);
  const [allResources, setAllResources] = useState<DirectResource[]>([]);
  const [resourcesLoaded, setResourcesLoaded] = useState(false);
  const [renovationProjects, setRenovationProjects] = useState<DirectResource[]>([]);
  const [renovationProjectsLoaded, setRenovationProjectsLoaded] = useState(false);
  const navigate = useNavigate();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const inputWrapperRef = useRef<HTMLDivElement>(null);
  const [selectedResource, setSelectedResource] = useState<DirectResource | null>(
    null,
  );

  // Expose focusInput method to parent components
  useImperativeHandle(ref, () => ({
    focusInput: () => {
      // Try multiple approaches to focus the input
      if (inputRef.current) {
        inputRef.current.focus();
        return;
      }
      
      // Fallback: find input element in the wrapper
      if (inputWrapperRef.current) {
        const inputElement = inputWrapperRef.current.querySelector('input');
        if (inputElement) {
          inputElement.focus();
          return;
        }
      }
      
      // Last resort: try to find any input in the dropdown
      const searchInput = document.querySelector('[placeholder*="Zoek een contact"]') as HTMLInputElement;
      if (searchInput) {
        searchInput.focus();
      }
    }
  }), []);
  const [searchMode, setSearchMode] = useState<SearchMode>(mode);
  const [apiData, setApiData] = useState<any[]>([]); // needs to have a name field from the API
  const { session } = useSession();
  const [quickCreateData, setQuickCreateData] = useState<QuickCreateFormData>({});
  const [quickCreateMode, setQuickCreateMode] = useState<QuickCreateMode>('none');
  const [isCreating, setIsCreating] = useState(false);
  const [expandedBuildings, setExpandedBuildings] = useState<Set<string>>(new Set());
  const [ellaChatboxOpen, setEllaChatboxOpen] = useState(false);
  const [selectedProjectForChat, setSelectedProjectForChat] = useState<DirectResource | null>(null);
  const [buildingContactIds, setBuildingContactIds] = useState<Set<string>>(buildingContactIdsCache ?? new Set());
  const [buildingContactsByBuilding, setBuildingContactsByBuilding] = useState<Record<string, { entityId: number; name: string; type: 'contact' | 'company'; reason: string }[]>>(buildingContactsByBuildingCache ?? {});

  // Fetch renovation projects
  const fetchRenovationProjects = async (): Promise<DirectResource[]> => {
    try {
      const response = await fetchWithAuth(
        `${API_URL}/renovation/projects`,
        { method: "GET" },
        session
      );
      
      if (!response.ok) throw new Error('Failed to fetch renovation projects');
      
      const projects = await response.json();
      
      // Transform renovation projects to DirectResource format
      return projects.map((project: any) => ({
        id: project.id,
        resourceId: project.id.toString(),
        type: 'renovation-project',
        name: project.title,
        metadata1: `RENO-${project.id}`,
        metadata2: project.status,
        year: project.dates?.find((d: any) => d.type === 'Verwachte startdatum')?.date 
          ? new Date(project.dates.find((d: any) => d.type === 'Verwachte startdatum').date).getFullYear() 
          : new Date().getFullYear(),
        // Budget now comes from investment item assignments
        budget: project.parts?.reduce((sum: number, part: any) => {
          return sum + (part.investmentAssignments?.reduce((partSum: number, assignment: any) =>
            partSum + Number(assignment.assignedAmount || 0), 0) || 0);
        }, 0) || 0,
        invoicedAmount: project.parts?.reduce((sum: number, part: any) => {
          const partTotal = part.documentLines
            ?.filter((line: any) => line.document.type === 'INVOICE')
            ?.reduce((lineSum: number, line: any) => {
              return lineSum + Number(line.totalInclVat || 0);
            }, 0) || 0;
          return sum + partTotal;
        }, 0) || 0,
        responsibleUserId: project.responsibleUserId,
        responsibleUser: project.responsibleUser,
        status: project.status,
        buildingId: project.building?.id
      }));
    } catch (error) {
      console.error('Error fetching renovation projects:', error);
      return [];
    }
  };

  // Efficient search function using useMemo for performance.
  // Why scoring: previous version used `includes` + early break at 50 items, iterating
  // contacts → companies → buildings → units. For queries like "ERNE", many contacts
  // (e.g. "Vernes", "Werner") matched first and filled the 50-slot budget before any
  // building/unit could surface — forcing users to type extra characters (postal code,
  // dash) just to push contacts out of the way. Ranking by relevance instead lets a
  // building whose name starts with the query always win over a contact that merely
  // contains it as a substring.
  const performLocalSearch = useMemo(() => {
    return (term: string, resources: DirectResource[]) => {
      if (!term || term.length < 2) return [];

      const termLower = term.toLowerCase();
      const scored: { resource: DirectResource; score: number }[] = [];

      try {
        for (const resource of resources) {
          if (resourceFilter.length > 0 && !resourceFilter.includes(resource.type as ResourceType)) {
            continue;
          }

          if (metadataFilter) {
            const hasMetadataMatch =
              resource.metadata1?.includes(metadataFilter) ||
              resource.metadata2?.includes(metadataFilter);
            if (!hasMetadataMatch) {
              continue;
            }
          }

          const nameLower = (resource.name || '').toLowerCase();
          const resourceIdLower = (resource.resourceId || '').toLowerCase();
          const meta1Lower = (resource.metadata1 || '').toLowerCase();
          const meta2Lower = (resource.metadata2 || '').toLowerCase();
          const renoCode = resource.type === 'renovation-project' ? `reno-${resource.id}`.toLowerCase() : '';

          let score = 0;

          if (nameLower.startsWith(termLower)) score += 1000;
          else if (nameLower.includes(termLower)) score += 500;

          if (resourceIdLower && resourceIdLower !== nameLower) {
            if (resourceIdLower.startsWith(termLower)) score += 200;
            else if (resourceIdLower.includes(termLower)) score += 100;
          }

          if (meta1Lower.includes(termLower)) score += 50;
          if (meta2Lower.includes(termLower)) score += 25;

          if (renoCode && renoCode.includes(termLower)) score += 200;

          // Buildings and units are the primary navigation targets in this app —
          // boost them so they outrank substring matches in contact names.
          if (score > 0 && (resource.type === 'building' || resource.type === 'unit')) {
            score = Math.round(score * 1.5);
          }

          if (score > 0) {
            scored.push({ resource, score });
          }
        }

        scored.sort((a, b) => b.score - a.score);
        return scored.slice(0, 50).map((s) => s.resource);
      } catch (error) {
        console.error('Error performing local search:', error);
        throw error;
      }
    };
  }, [resourceFilter, metadataFilter]);

  useEffect(() => {
    console.log("DirectSearchComponent mounted");
    setIsEditMode(editMode);
  }, [editMode]);

  useEffect(() => {
    setSearchMode(mode);
  }, [mode]);

  // Load resources with caching
  useEffect(() => {
    const loadResources = async () => {
      try {
        if (!api && !customData) {
          setIsLoading(true);
          
          // Use cached resources if available
          if (resourcesCache) {
            setAllResources(resourcesCache);
            setResourcesLoaded(true);
            setIsLoading(false);
            return;
          }

          // If there's already a cache promise in progress, wait for it
          if (cachePromise) {
            const resources = await cachePromise;
            setAllResources(resources);
            setResourcesLoaded(true);
            setIsLoading(false);
            return;
          }

          // Create new cache promise
          cachePromise = fetchResourcesFromAPI(session);
          const resources = await cachePromise;
          
          // Cache the results
          resourcesCache = resources;
          console.log('Resources loaded and cached:', resources.length);
          setAllResources(resources);
          setResourcesLoaded(true);
          setIsLoading(false);
          
          // Clear the promise
          cachePromise = null;
        }
      } catch (error) {
        console.error("Error loading resources:", error);
        setIsLoading(false);
        cachePromise = null;
      }
    };

    loadResources();
  }, [session, api, customData]);

  // Load renovation projects with caching (only if not excluded)
  useEffect(() => {
    const loadRenovationProjects = async () => {
      try {
        if (!api && !customData && session && !excludeRenovationProjects) {
          // Use cached renovation projects if available
          if (renovationProjectsCache) {
            setRenovationProjects(renovationProjectsCache);
            setRenovationProjectsLoaded(true);
            return;
          }

          // If there's already a cache promise in progress, wait for it
          if (renovationProjectsCachePromise) {
            const projects = await renovationProjectsCachePromise;
            setRenovationProjects(projects);
            setRenovationProjectsLoaded(true);
            return;
          }

          // Create new cache promise
          renovationProjectsCachePromise = fetchRenovationProjects();
          const projects = await renovationProjectsCachePromise;
          
          // Cache the results
          renovationProjectsCache = projects;
          console.log('Renovation projects loaded and cached:', projects.length);
          setRenovationProjects(projects);
          setRenovationProjectsLoaded(true);
          
          // Clear the promise
          renovationProjectsCachePromise = null;
        } else if (excludeRenovationProjects) {
          // If renovation projects are excluded, mark as loaded with empty array
          setRenovationProjects([]);
          setRenovationProjectsLoaded(true);
        }
      } catch (error) {
        console.error("Error loading renovation projects:", error);
        renovationProjectsCachePromise = null;
      }
    };

    loadRenovationProjects();
  }, [session, api, customData, excludeRenovationProjects]);

  // Load building contact IDs and by-building map with caching (single fetch)
  useEffect(() => {
    const loadBuildingContactData = async () => {
      if (!session) return;
      try {
        if (buildingContactIdsCache && buildingContactsByBuildingCache) {
          setBuildingContactIds(buildingContactIdsCache);
          setBuildingContactsByBuilding(buildingContactsByBuildingCache);
          return;
        }
        if (buildingContactIdsCachePromise) {
          await buildingContactIdsCachePromise;
          if (buildingContactIdsCache) setBuildingContactIds(buildingContactIdsCache);
          if (buildingContactsByBuildingCache) setBuildingContactsByBuilding(buildingContactsByBuildingCache);
          return;
        }
        buildingContactIdsCachePromise = fetchWithAuth(
          `${API_URL}/contact/building-contacts-overview`,
          { method: "GET" },
          session
        ).then(async (res) => {
          if (!res.ok) return;
          const rows: {
            type: string;
            buildingId: string;
            contactId: number | null;
            contactFirstName: string | null;
            contactLastName: string | null;
            companyId: number | null;
            companyName: string | null;
            reason: string;
          }[] = await res.json();

          const ids = new Set<string>();
          const byBuilding: Record<string, { entityId: number; name: string; type: 'contact' | 'company'; reason: string }[]> = {};

          for (const row of rows) {
            const entityId = row.type === "contact" ? row.contactId : row.companyId;
            if (!entityId) continue;
            ids.add(`${row.type}-${entityId}`);
            if (!byBuilding[row.buildingId]) byBuilding[row.buildingId] = [];
            byBuilding[row.buildingId].push({
              entityId,
              name: row.type === "contact"
                ? `${row.contactFirstName ?? ""} ${row.contactLastName ?? ""}`.trim()
                : (row.companyName ?? ""),
              type: row.type as 'contact' | 'company',
              reason: row.reason,
            });
          }

          buildingContactIdsCache = ids;
          buildingContactsByBuildingCache = byBuilding;
        });
        await buildingContactIdsCachePromise;
        buildingContactIdsCachePromise = null;
        if (buildingContactIdsCache) setBuildingContactIds(buildingContactIdsCache);
        if (buildingContactsByBuildingCache) setBuildingContactsByBuilding(buildingContactsByBuildingCache);
      } catch (error) {
        console.error("Error loading building contact data:", error);
        buildingContactIdsCachePromise = null;
      }
    };

    loadBuildingContactData();
  }, [session]);

  // Handle API data
  useEffect(() => {
    if (api) {
      const fetchData = async () => {
        try {
          setIsLoading(true);
          const response = await fetchWithAuth(api, { method: "GET" }, session);
          const data = await response.json();
          setApiData(data);
          setIsLoading(false);

          if (data.length === 1) {
            handleSelect(data[0]);
          }
        } catch (error) {
          console.error("Error fetching data:", error);
          setIsLoading(false);
        }
      };

      fetchData();
    }
  }, [api, session]);

  // Handle initial value selection
  useEffect(() => {
    if (!resourcesLoaded) return;

    if (initialValue && initialValueType !== "id") {
      const foundResource = allResources.find(
        (resource) => resource.name === initialValue,
      );
      if (foundResource) {
        setSelectedResource(foundResource);
        setSearchTerm(foundResource.name);
      }
    }
    
    if (initialValue && initialValueType === "id") {
      const foundResource = allResources.find(
        (resource) => resource.id === initialValue,
      );
      if (foundResource) {
        setSelectedResource(foundResource);
        setSearchTerm(foundResource.name);
      }
    }
    
    if (initialResource) {
      setSelectedResource(initialResource as DirectResource);
      setSearchTerm(initialResource.name as string);
    }
  }, [initialValue, initialValueType, allResources, initialResource, resourcesLoaded]);

  useEffect(() => {
    const handleClickOutside = (event: any) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowResults(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Optimized debounced search - now purely local
  const debouncedSearch = useMemo(
    () => debounce((term: string) => {
      if (term.length < 2) {
        setSearchResults([]);
        setShowResults(false);
        return;
      }

      let filteredResults: DirectResource[] = [];

      if (customData && customData.length > 0) {
        // Handle custom data
        const termLower = term.toLowerCase();
        filteredResults = customData.filter((item) => {
          const searchableFields = [
            item.firstName,
            item.lastName,
            item.id?.toString(),
            item.searchTerm,
            item.name,
            item.type,
            item.subType
          ];
          
          return searchableFields.some(field => 
            field && field.toLowerCase().includes(termLower)
          );
        }).slice(0, 50);
      } else if (apiData.length > 0) {
        // Handle API data
        filteredResults = performLocalSearch(term, apiData);
      } else if (resourcesLoaded || renovationProjectsLoaded) {
        // Combine regular resources and renovation projects (only if not excluded)
        const combinedResources = [
          ...(resourcesLoaded ? allResources : []),
          ...(renovationProjectsLoaded && !excludeRenovationProjects ? renovationProjects : [])
        ];
        filteredResults = performLocalSearch(term, combinedResources);
      }

      console.log('Search performed:', term, 'Results:', filteredResults.length);
      setSearchResults(filteredResults);
      setShowResults(true);
    }, 150), // Reduced debounce time for more responsive feel
    [customData, apiData, allResources, resourcesLoaded, renovationProjects, renovationProjectsLoaded, performLocalSearch, excludeRenovationProjects]
  );

  const handleSearch = (term: string) => {
    if (term === "") {
      setSearchTerm("");
      setSearchResults([]);
      setShowResults(false);
      isCleared?.();
      return;
    }

    setSearchTerm(term);
    
    if (!term.trim()) {
      setSearchResults([]);
      setShowResults(false);
      return;
    }
    
    debouncedSearch(term);
  };

  const getIcon = (type: string) => {
    switch (type) {
      case "building":
        return <Buildings weight="duotone" size={20} />;
      case "contact":
        return <User weight="duotone" size={20} />;
      case "company":
        return <UsersThree weight="duotone" size={20} />;
      case "renovation-project":
        return <HardHat weight="duotone" size={20} />;
      default:
        return <UsersThree weight="duotone" size={20} />;
    }
  };

  const groupedResults = useMemo(() => {
    const groups = searchResults.reduce(
      (groups, result) => {
        (groups[result.type] = groups[result.type] || []).push(result);
        return groups;
      },
      {} as Record<string, DirectResource[]>,
    );

    // Sort contacts to show current tenants first
    if (groups['contact']) {
      groups['contact'].sort((a, b) => {
        // Current tenants (with "Huurder" in metadata2) come first
        const aIsCurrent = a.metadata2?.includes('Huurder ') && !a.metadata2?.includes('Voormalige') && !a.metadata2?.includes('Ex-huurder');
        const bIsCurrent = b.metadata2?.includes('Huurder ') && !b.metadata2?.includes('Voormalige') && !b.metadata2?.includes('Ex-huurder');
        
        if (aIsCurrent && !bIsCurrent) return -1;
        if (!aIsCurrent && bIsCurrent) return 1;
        return 0;
      });
    }

    // When buildings are in results, inject their gebouwcontacten into contact/company groups
    if (groups['building']) {
      for (const building of groups['building']) {
        const linked = buildingContactsByBuilding[building.resourceId] ?? [];
        for (const bc of linked) {
          const groupKey = bc.type;
          if (!groups[groupKey]) groups[groupKey] = [];
          const alreadyPresent = groups[groupKey].some(r => r.resourceId === bc.entityId.toString());
          if (alreadyPresent) continue;
          // Prefer full resource from allResources, fall back to minimal entry
          const full = allResources.find(r => r.type === bc.type && r.resourceId === bc.entityId.toString());
          groups[groupKey].push(full ?? {
            id: bc.entityId,
            resourceId: bc.entityId.toString(),
            type: bc.type,
            name: bc.name,
          });
        }
      }
    }

    // Return groups in the specified order: Buildings, Renovation Projects, Units, Contacts, Companies
    const orderedGroups: Record<string, DirectResource[]> = {};
    const typeOrder = ['building', 'renovation-project', 'unit', 'contact', 'company'];
    
    typeOrder.forEach(type => {
      if (groups[type]) {
        orderedGroups[type] = groups[type];
      }
    });

    // Add any remaining types not in the predefined order
    Object.keys(groups).forEach(type => {
      if (!typeOrder.includes(type)) {
        orderedGroups[type] = groups[type];
      }
    });

    return orderedGroups;
  }, [searchResults, buildingContactsByBuilding, allResources]);

  const handleNavigation = (resource: {
    id: number;
    resourceId: string;
    type: string;
    name: string;
  }) => {
    setSearchTerm(resource.name);
    setShowResults(false);
    
    if (resource.type === 'renovation-project') {
      // For renovation projects, navigate to the building's renovation tab with highlighting
      const projectData = renovationProjects.find(p => p.id === resource.id);
      if (projectData && projectData.buildingId) {
        // Navigate to building renovation dedicated route with query parameters for highlighting
        navigate(`/building/${projectData.buildingId}/renovation?highlightProject=${resource.id}&openYear=${projectData.year}`);
      } else {
        // Fallback to renovation project page
        navigate(`/renovation/${resource.resourceId}`);
      }
    } else {
      navigate(`/${resource.type}/${resource.resourceId}`);
    }
    onMobileNavSelect?.();
  };

  const handleSelect = async (resource: any) => {
    if (resource && resource.name) {
      setSearchTerm(resource.name);
      setShowResults(false);

      let selectedResource = {
        id: resource.id,
        resourceId: resource.resourceId,
        type: resource.type,
        name: resource.name,
        metadata1: resource.metadata1,
        metadata2: resource.metadata2,
        sharePointFolderId: resource.sharePointFolderId,
        sharePointFolderUrl: resource.sharePointFolderUrl,
        sharePointPicturesFolderId: resource.sharePointPicturesFolderId,
        sharePointPicturesFolderUrl: resource.sharePointPicturesFolderUrl,
      };

      // Details (o.a. het e-mailadres) meegeven voor contactpersonen én firma's, zodat
      // consumenten zoals de mailmodule er direct naartoe kunnen mailen.
      if (resource.type === "contact" || resource.type === "company") {
        const detailUrl =
          resource.type === "contact"
            ? `${API_URL}/contact/${resource.resourceId}`
            : `${API_URL}/contact/company/${resource.resourceId}`;
        try {
          const response = await fetchWithAuth(detailUrl, { method: "GET" }, session);
          if (response.ok) {
            const contactData = await response.json();
            (selectedResource as any) = {
              ...selectedResource,
              contactDetails: contactData,
            };
          } else {
            console.error(
              `Failed to fetch ${resource.type} details (${response.status}) for ${resource.resourceId}`,
            );
            Sentry.captureMessage("DirectSearchComponent: details ophalen mislukt", {
              level: "warning",
              extra: { type: resource.type, resourceId: resource.resourceId, status: response.status },
            });
          }
        } catch (error) {
          console.error("Error fetching contact details:", error);
          Sentry.captureException(error, {
            tags: { component: "DirectSearchComponent" },
            extra: { type: resource.type, resourceId: resource.resourceId },
          });
        }
      }

      onSelect?.(selectedResource);
      setSelectedResource(selectedResource);
    }
  };

  const handleQuickCreate = async () => {
    try {
      setIsCreating(true);

      const data = {
        type: quickCreateMode === 'company' ? "company" : "contact",
        ...(quickCreateMode === 'company' ? {
          companyName: quickCreateData.companyName,
          email: quickCreateData.email,
          phone: quickCreateData.phone,
          address: quickCreateData.address
        } : {
          firstName: quickCreateData.firstName,
          lastName: quickCreateData.lastName,
          email: quickCreateData.email,
          phone: quickCreateData.phone
        })
      };

      const response = await fetchWithAuth(
        `${API_URL}/contact/quick-create`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(data)
        },
        session
      );

      if (!response.ok) {
        throw new Error('Failed to create');
      }

      const result = await response.json();

      // Check if a duplicate was found
      if (result.isDuplicate) {
        // Show toast with duplicate info and link to existing entity
        const entityType = result.type === 'company' ? 'bedrijf' : 'contact';
        const entityUrl = `/${result.type}/${result.resourceId}`;

        toast.info(
          <div className="flex flex-col gap-2">
            <span className="font-medium">{result.duplicateReason}</span>
            <button
              onClick={() => {
                navigate(entityUrl);
                onMobileNavSelect?.();
              }}
              className="text-left text-sm text-moss-primary hover:underline"
            >
              Ga naar bestaand {entityType}: {result.name}
            </button>
          </div>,
          {
            autoClose: 8000,
            closeOnClick: false,
          }
        );

        // Still select the existing entity so user can continue their workflow
        handleSelect(result);
      } else {
        // New entity created
        toast.success(
          quickCreateMode === 'company'
            ? `Bedrijf "${result.name}" aangemaakt`
            : `Contact "${result.name}" aangemaakt`,
          { autoClose: 3000 }
        );

        // Use the created entity
        handleSelect(result);
      }

      // Invalidate cache so new/existing resource shows up correctly
      resourcesCache = null;

      // Reset form
      setQuickCreateMode('none');
      setQuickCreateData({});

    } catch (error) {
      console.error('Error creating:', error);
      toast.error('Er is een fout opgetreden bij het aanmaken. Probeer het opnieuw.');
    } finally {
      setIsCreating(false);
    }
  };

  const handleEllaChatbox = (project: DirectResource) => {
    setSelectedProjectForChat(project);
    setEllaChatboxOpen(true);
  };

  const formatCurrency = (amount: number): string => {
    return `€${amount.toLocaleString('nl-BE', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  };

  // Get renovation projects for a specific building
  const getBuildingProjects = (buildingId: string): DirectResource[] => {
    return renovationProjects.filter(project => project.buildingId === buildingId);
  };

  // Get project status color and label
  const getProjectStatusInfo = (project: DirectResource) => {
    const currentYear = new Date().getFullYear();
    const projectYear = project.year || currentYear;
    
    // Determine if project is past, current, or future based on year and status
    if (projectYear < currentYear || 
        ['Afgerond', 'Volledig gefactureerd'].includes(project.status || '')) {
      return {
        color: 'bg-neutral-100 text-neutral-600 border-neutral-200',
        label: 'Afgerond',
        icon: '✓'
      };
    } else if (projectYear === currentYear && 
               ['In uitvoering', 'Leverancier gekozen'].includes(project.status || '')) {
      return {
        color: 'bg-moss-primary/10 text-moss-primary border-moss-primary/30',
        label: 'Actief',
        icon: '▶'
      };
    } else {
      return {
        color: 'bg-brick-primary/10 text-brick-primary border-brick-primary/30',
        label: 'Gepland',
        icon: '📅'
      };
    }
  };

  return (
    <div className={`relative h-fit ${classNames}`} ref={dropdownRef}>
      {searchMode === SearchMode.SEARCH_AND_PRESENT && !isEditMode ? (
        selectedResource ? (
          <div className="flex justify-between gap-2">
            <a
              href={`/${selectedResource.type}/${selectedResource.resourceId}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              {selectedResource.name}
            </a>
            <div className="flex gap-6">
              {hasEditToggle ? (
                <ButtonNew
                  variant="primaryMoss"
                  onClick={() => setIsEditMode((prev) => !prev)}
                  icon={<Edit />}
                  label={"Bewerken"}
                />
              ) : null}
            </div>
          </div>
        ) : (
          <div className="flex justify-between gap-2">
            <p className="p-2">Niet ingevuld</p>
            {hasEditToggle ? (
              <div className="flex gap-6">
                <ButtonNew
                  variant="primaryMoss"
                  onClick={() => setIsEditMode((prev) => !prev)}
                  icon={<Edit />}
                  label={"Bewerken"}
                />
              </div>
            ) : null}
          </div>
        )
      ) : (
        <>
          <div className="flex gap-2" ref={inputWrapperRef}>
            <Input
              ref={inputRef}
              startContent={
                isLoading ? (
                  <Spinner size="sm" color="warning" />
                ) : (
                  <MagnifyingGlass weight="duotone" size={18} />
                )
              }
              placeholder={
                label || "Zoek een contact, een gebouw, een project, ..."
              }
              classNames={{
                input:
                  "text-neutral-primary placeholder:text-neutral-secondary-mid",
                inputWrapper:
                  "p-2 min-h-fit h-fit rounded-full bg-white shadow-form-element data-[hover=true]:bg-white group-data-[focus=true]:bg-white data-[hover=true]:shadow-form-element-hv group-data-[focus=true]:shadow-form-element-fc",
              }}
              value={searchTerm as string}
              onChange={(e) => handleSearch(e.target.value)}
            />

            {showResults && (
              <div className="absolute left-0 top-full z-50 mt-2 max-h-[70vh] w-full overflow-y-auto rounded-lg border border-neutral-primary/20 bg-white p-2 shadow-md">
                {searchResults.length > 0 ? (
                  Object.entries(groupedResults).map(([type, results]) => (
                    <div key={type} className="mb-4">
                      <div className="mb-2 text-sm font-medium text-neutral-primary uppercase tracking-wide">
                        {type}
                      </div>
                      <div className="flex flex-col gap-1">
                        {results.map((result) => (
                          result.type === 'renovation-project' ? (
                            <div
                              key={result.id}
                              className="flex items-start gap-3 w-full rounded-lg p-3 border border-neutral-100 hover:border-moss-primary/30 transition-colors bg-linear-to-r from-moss-primary/5 to-transparent"
                            >
                              <span className="text-moss-primary mt-0.5">
                                {getIcon(result.type)}
                              </span>
                              <div className="flex flex-col min-w-0 flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <span className="text-sm text-neutral-primary font-semibold">
                                    {result.name}
                                  </span>
                                  <span className="text-xs bg-moss-primary/10 text-moss-primary px-2 py-0.5 rounded-full font-medium">
                                    {result.metadata1}
                                  </span>
                                  {result.buildingId && (
                                    <span className="text-xs bg-neutral-100 text-neutral-600 px-2 py-0.5 rounded-full font-medium">
                                      Building {result.buildingId}
                                    </span>
                                  )}
                                </div>
                                
                                <div className="flex items-center gap-4 text-xs text-neutral-600 mb-2">
                                  <div className="flex items-center gap-1">
                                    <span>📅</span>
                                    <span>{result.year}</span>
                                  </div>
                                  <div className="flex items-center gap-1">
                                    <CurrencyEur size={12} />
                                    <span>{formatCurrency(result.budget || 0)} budget</span>
                                  </div>
                                  {(result.invoicedAmount || 0) > 0 && (
                                    <div className="flex items-center gap-1">
                                      <span>💳</span>
                                      <span>{formatCurrency(result.invoicedAmount || 0)} gefactureerd</span>
                                    </div>
                                  )}
                                  <div className="flex items-center gap-1">
                                    {result.responsibleUser?.image_url ? (
                                      <img
                                        src={result.responsibleUser.image_url}
                                        alt={`${result.responsibleUser.first_name} ${result.responsibleUser.last_name}`}
                                        className="w-4 h-4 rounded-full object-cover"
                                      />
                                    ) : (
                                      <div className="w-4 h-4 bg-neutral-200 rounded-full flex items-center justify-center">
                                        <User size={8} />
                                      </div>
                                    )}
                                    <span>{result.responsibleUser ? `${result.responsibleUser.first_name} ${result.responsibleUser.last_name}` : 'Niet toegewezen'}</span>
                                  </div>
                                </div>

                                <div className="flex items-center gap-2">
                                  <button
                                    className="flex items-center gap-1 px-3 py-1 rounded-lg bg-moss-primary text-white text-xs font-medium hover:bg-moss-primary/90 transition-colors"
                                    onClick={() => onSelect ? handleSelect(result) : handleNavigation({
                                      id: result.id as number,
                                      resourceId: result.resourceId,
                                      type: result.type,
                                      name: result.name,
                                    })}
                                  >
                                    <span>Project bekijken</span>
                                  </button>
                                  <button
                                    className="flex items-center gap-1 px-3 py-1 rounded-lg bg-brick-primary text-white text-xs font-medium hover:bg-brick-primary/90 transition-colors"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleEllaChatbox(result);
                                    }}
                                  >
                                    <ChatCircle size={12} />
                                    <span>Ella uitleg</span>
                                  </button>
                                </div>
                              </div>
                            </div>
                          ) : result.type === 'building' ? (
                            // Enhanced building result with renovation projects
                            (<div
                              key={result.id}
                              className="rounded-lg border border-neutral-100 hover:border-moss-primary/30 transition-colors bg-linear-to-r from-neutral-50/50 to-transparent overflow-hidden"
                            >
                              <button
                                className="flex items-start gap-3 w-full p-3 text-left hover:bg-neutral-50/50 transition-colors"
                                onClick={() => onSelect ? handleSelect(result) : handleNavigation({
                                  id: result.id as number,
                                  resourceId: result.resourceId,
                                  type: result.type,
                                  name: result.name,
                                })}
                              >
                                <span className="text-moss-primary mt-0.5">
                                  {getIcon(result.type)}
                                </span>
                                <div className="flex flex-col min-w-0 flex-1">
                                  <div className="flex items-center gap-2 mb-1">
                                    <span className="text-sm text-neutral-primary font-semibold">
                                      {result.name}
                                    </span>
                                    {result.metadata1 && (
                                      <span className="text-xs bg-neutral-100 text-neutral-600 px-2 py-0.5 rounded-full font-medium">
                                        {result.metadata1}
                                      </span>
                                    )}
                                  </div>
                                  {result.metadata2 && (
                                    <span className="text-xs text-neutral-secondary mb-2">
                                      {result.metadata2}
                                    </span>
                                  )}
                                </div>
                              </button>
                              {/* Renovation Projects for this building */}
                              {(() => {
                                const buildingProjects = getBuildingProjects(result.resourceId);
                                if (buildingProjects.length === 0) return null;
                                
                                const isExpanded = expandedBuildings.has(result.resourceId);
                                const projectsToShow = isExpanded ? buildingProjects : buildingProjects.slice(0, 3);
                                const hasMoreProjects = buildingProjects.length > 3;
                                
                                return (
                                  <div className="border-t border-neutral-100 bg-neutral-50/30 px-3 py-2">
                                    <div className="flex items-center justify-between mb-2">
                                      <div className="flex items-center gap-2">
                                        <HardHat size={12} className="text-moss-primary" />
                                        <span className="text-xs font-medium text-moss-primary">
                                          {buildingProjects.length} Renovatieproject{buildingProjects.length !== 1 ? 'en' : ''}
                                        </span>
                                      </div>
                                      {hasMoreProjects && (
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setExpandedBuildings(prev => {
                                              const newSet = new Set(prev);
                                              if (newSet.has(result.resourceId)) {
                                                newSet.delete(result.resourceId);
                                              } else {
                                                newSet.add(result.resourceId);
                                              }
                                              return newSet;
                                            });
                                          }}
                                          className="text-xs text-moss-primary hover:text-moss-primary/80 font-medium transition-colors"
                                        >
                                          {isExpanded ? 'Toon minder' : `+${buildingProjects.length - 3} meer`}
                                        </button>
                                      )}
                                    </div>
                                    <div className="flex flex-wrap gap-1.5">
                                      {projectsToShow.map((project) => {
                                        const statusInfo = getProjectStatusInfo(project);
                                        return (
                                          <button
                                            key={project.id}
                                            className={`flex items-center gap-1.5 text-xs px-2 py-1.5 rounded-lg border transition-all duration-200 hover:scale-105 hover:shadow-sm ${statusInfo.color}`}
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleNavigation({
                                                id: project.id as number,
                                                resourceId: project.resourceId,
                                                type: project.type,
                                                name: project.name,
                                              });
                                            }}
                                            title={`RENO-${project.id} - ${project.name} - ${project.status} (${project.year})`}
                                          >
                                            {/* Responsible user avatar */}
                                            {project.responsibleUser?.image_url ? (
                                              <img
                                                src={project.responsibleUser.image_url}
                                                alt={`${project.responsibleUser.first_name} ${project.responsibleUser.last_name}`}
                                                className="w-4 h-4 rounded-full object-cover shrink-0"
                                                onError={(e) => {
                                                  const target = e.target as HTMLImageElement;
                                                  target.style.display = 'none';
                                                }}
                                              />
                                            ) : (
                                              <div className="w-4 h-4 rounded-full bg-neutral-300 flex items-center justify-center text-neutral-600 text-[8px] shrink-0">
                                                ?
                                              </div>
                                            )}
                                            
                                            {/* Status icon */}
                                            <span className="shrink-0">{statusInfo.icon}</span>
                                            
                                            {/* RENO code */}
                                            <span className="font-mono font-medium">RENO-{project.id}</span>
                                            
                                            {/* Project name (truncated) */}
                                            <span className="truncate max-w-[80px]">
                                              {project.name}
                                            </span>
                                          </button>
                                        );
                                      })}
                                    </div>
                                  </div>
                                );
                              })()}
                            </div>)
                          ) : (
                            // Generic result for other types
                            (<button
                              key={result.id}
                              className="flex items-start gap-3 w-full rounded-lg p-2 text-left hover:bg-neutral-50 transition-colors"
                              onClick={() => onSelect ? handleSelect(result) : handleNavigation({
                                id: result.id as number,
                                resourceId: result.resourceId,
                                type: result.type,
                                name: result.name,
                              })}
                            >
                              <span className="text-neutral-primary mt-0.5">
                                {getIcon(result.type)}
                              </span>
                              <div className="flex flex-col min-w-0 flex-1">
                                <div className="flex items-center gap-1.5">
                                  <span className="text-sm text-neutral-primary font-medium">
                                    {result.name}
                                  </span>
                                  {result.type === "unit" && result.outstandingDebt && result.outstandingDebt > 0 && (
                                    <span className="shrink-0 text-xs font-semibold px-1.5 py-0.5 rounded bg-brick-primary/10 text-brick-primary border border-brick-primary/20">
                                      {formatCurrency(result.outstandingDebt)} openstaand
                                    </span>
                                  )}
                                  {(result.type === "contact" || result.type === "company") &&
                                    buildingContactIds.has(`${result.type}-${result.resourceId}`) && (
                                      <Wrench
                                        weight="duotone"
                                        size={13}
                                        className="shrink-0 text-moss-primary"
                                      />
                                    )}
                                </div>
                                {result.metadata1 && (
                                  <span className="text-xs text-neutral-secondary">
                                    {result.metadata1}
                                  </span>
                                )}
                                {result.metadata2 && (
                                  <span className="text-xs text-neutral-secondary">
                                    {result.metadata2}
                                  </span>
                                )}
                              </div>
                            </button>)
                          )
                        ))}
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="p-2">
                    {quickCreateMode === 'none' ? (
                      <div className="flex flex-col gap-2">
                        <p className="text-sm text-neutral-secondary">
                          Geen resultaten gevonden.
                        </p>
                        <div className="flex gap-2">
                          {resourceFilter.includes("contact" as ResourceType) && (
                            <button
                              className="flex items-center gap-2 rounded-lg bg-secondary px-3 py-2 text-sm text-white hover:bg-secondary/90 transition-colors"
                              onClick={() => {
                                setQuickCreateMode('contact');
                                setQuickCreateData({});
                              }}
                            >
                              <User className="h-4 w-4" />
                              Nieuw Contact
                            </button>
                          )}
                          {resourceFilter.includes("company" as ResourceType) && (
                            <button
                              className="flex items-center gap-2 rounded-lg bg-brick-primary px-3 py-2 text-sm text-white hover:bg-brick-primary/90 transition-colors"
                              onClick={() => {
                                setQuickCreateMode('company');
                                setQuickCreateData({});
                              }}
                            >
                              <Buildings className="h-4 w-4" />
                              Nieuw Bedrijf
                            </button>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="flex flex-col gap-3">
                        {quickCreateMode === 'contact' && (
                          <div 
                            className="rounded-lg bg-neutral-50 p-4"
                          >
                            <div className="mb-4 flex flex-col">
                              <h3 className="text-lg font-medium text-neutral-900">
                                Contact Gegevens
                              </h3>
                              <p className="text-sm text-neutral-600">
                                Vul minimaal de naam in
                              </p>
                            </div>

                            <div className="flex flex-col gap-3">
                              <div className="flex gap-3">
                                <div className="flex-1">
                                  <label className="mb-1 block text-sm font-medium text-neutral-700">
                                    Voornaam <span className="text-red-500">*</span>
                                  </label>
                                  <input
                                    type="text"
                                    placeholder="John"
                                    value={quickCreateData?.firstName || ''}
                                    onChange={(e) => {
                                      setQuickCreateData(prev => ({ ...prev, firstName: e.target.value }));
                                    }}   
                                    className="w-full rounded-lg border border-neutral-200 bg-white p-2 text-sm text-neutral-700 shadow-sm focus:border-primary focus:outline-hidden focus:ring-1 focus:ring-primary"
                                  />
                                </div>
                                <div className="flex-1">
                                  <label className="mb-1 block text-sm font-medium text-neutral-700">
                                    Achternaam <span className="text-red-500">*</span>
                                  </label>
                                  <input
                                    type="text"
                                    placeholder="Doe"
                                    value={quickCreateData?.lastName || ''}
                                    onChange={(e) => setQuickCreateData(prev => ({ ...prev, lastName: e.target.value }))}
                                    className="w-full rounded-lg border border-neutral-200 bg-white p-2 text-sm text-neutral-700 shadow-sm focus:border-primary focus:outline-hidden focus:ring-1 focus:ring-primary"
                                  />
                                </div>
                              </div>

                              <div className="grid grid-cols-2 gap-3">
                                <div>
                                  <label className="mb-1 block text-sm font-medium text-neutral-700">
                                    Email
                                  </label>
                                  <input
                                    type="email"
                                    placeholder="john.doe@example.com"
                                    value={quickCreateData?.email || ''}
                                    onChange={(e) => setQuickCreateData(prev => ({ ...prev, email: e.target.value }))}
                                    className="w-full rounded-lg border border-neutral-200 bg-white p-2 text-sm text-neutral-700 shadow-sm focus:border-primary focus:outline-hidden focus:ring-1 focus:ring-primary"
                                  />
                                </div>
                                <div>
                                  <label className="mb-1 block text-sm font-medium text-neutral-700">
                                    Telefoon
                                  </label>
                                  <input
                                    type="tel"
                                    placeholder="+32 123 45 67 89"
                                    value={quickCreateData?.phone || ''}
                                    onChange={(e) => setQuickCreateData(prev => ({ ...prev, phone: e.target.value }))}
                                    className="w-full rounded-lg border border-neutral-200 bg-white p-2 text-sm text-neutral-700 shadow-sm focus:border-primary focus:outline-hidden focus:ring-1 focus:ring-primary"
                                  />
                                </div>
                              </div>
                            </div>
                          </div>
                        )}

                        {quickCreateMode === 'company' && (
                          <div className="rounded-lg bg-neutral-50 p-4">
                            <div className="mb-4 flex flex-col">
                              <h3 className="text-lg font-medium text-neutral-900">
                                Bedrijf Gegevens
                              </h3>
                              <p className="text-sm text-neutral-600">
                                Vul minimaal de bedrijfsnaam in
                              </p>
                            </div>

                            <div className="flex flex-col gap-3">
                              <div>
                                <label className="mb-1 block text-sm font-medium text-neutral-700">
                                  Bedrijfsnaam <span className="text-red-500">*</span>
                                </label>
                                <input
                                  type="text"
                                  placeholder="Acme Inc."
                                  value={quickCreateData?.companyName || ''}
                                  onChange={(e) => setQuickCreateData(prev => ({ ...prev, companyName: e.target.value }))}
                                  className="w-full rounded-lg border border-neutral-200 bg-white p-2 text-sm text-neutral-700 shadow-sm focus:border-primary focus:outline-hidden focus:ring-1 focus:ring-primary"
                                />
                              </div>

                              <div className="grid grid-cols-2 gap-3">
                                <div>
                                  <label className="mb-1 block text-sm font-medium text-neutral-700">
                                    Email
                                  </label>
                                  <input
                                    type="email"
                                    placeholder="info@acme.com"
                                    value={quickCreateData?.email || ''}
                                    onChange={(e) => setQuickCreateData(prev => ({ ...prev, email: e.target.value }))}
                                    className="w-full rounded-lg border border-neutral-200 bg-white p-2 text-sm text-neutral-700 shadow-sm focus:border-primary focus:outline-hidden focus:ring-1 focus:ring-primary"
                                  />
                                </div>
                                <div>
                                  <label className="mb-1 block text-sm font-medium text-neutral-700">
                                    Telefoon
                                  </label>
                                  <input
                                    type="tel"
                                    placeholder="+32 123 45 67 89"
                                    value={quickCreateData?.phone || ''}
                                    onChange={(e) => setQuickCreateData(prev => ({ ...prev, phone: e.target.value }))}
                                    className="w-full rounded-lg border border-neutral-200 bg-white p-2 text-sm text-neutral-700 shadow-sm focus:border-primary focus:outline-hidden focus:ring-1 focus:ring-primary"
                                  />
                                </div>
                              </div>

                              <AddressPicker
                                editMode={true}
                                label="Adres"
                                getAddress={(selectedPlace) => {
                                  if (selectedPlace) {
                                    setQuickCreateData(prev => ({
                                      ...prev,
                                      address: {
                                        formattedAddress: selectedPlace.description,
                                        placeId: selectedPlace.placeId
                                      }
                                    }));
                                  }
                                }}
                              />
                            </div>
                          </div>
                        )}

                        <div className="flex items-center justify-between border-t border-neutral-200 pt-3">
                          <Button
                            size="sm"
                            variant="light"
                            onPress={() => {
                              setQuickCreateMode('none');
                            }}
                          >
                            Annuleren
                          </Button>
                          <Button
                            color="primary"
                            size="sm"
                            isDisabled={
                              (quickCreateMode === 'contact' && (!quickCreateData?.firstName || !quickCreateData?.lastName)) ||
                              (quickCreateMode === 'company' && !quickCreateData?.companyName)
                            }
                            onPress={() => {
                              handleQuickCreate();
                            }}
                            isLoading={isCreating}
                          >
                            {isCreating ? 'Bezig met aanmaken...' : 'Aanmaken'}
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </>
      )}
      {/* Ella Chatbox Modal */}
      {ellaChatboxOpen && selectedProjectForChat && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full mx-4 shadow-2xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-linear-to-br from-brick-primary to-brick-primary/80 rounded-full flex items-center justify-center">
                <img
                  src="/kaylee-avatar.png" // You'll need to add Kaylee's avatar image
                  alt="Ella (Kaylee)"
                  className="w-8 h-8 rounded-full object-cover"
                  onError={(e) => {
                    // Fallback to ChatCircle icon if image fails
                    (e.target as HTMLImageElement).style.display = 'none';
                    (e.target as HTMLImageElement).nextElementSibling!.classList.remove('hidden');
                  }}
                />
                <ChatCircle size={20} className="text-white hidden" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-neutral-primary">Ella - AI Assistant</h3>
                <p className="text-sm text-neutral-600">Uitleg over renovatieproject</p>
              </div>
              <button
                onClick={() => setEllaChatboxOpen(false)}
                className="ml-auto p-2 rounded-full hover:bg-neutral-100 transition-colors"
              >
                ×
              </button>
            </div>
            
            <div className="bg-linear-to-r from-moss-primary/10 to-brick-primary/10 rounded-xl p-4 mb-4">
              <div className="flex items-center gap-2 mb-2">
                <HardHat size={16} className="text-moss-primary" />
                <span className="font-medium text-neutral-primary">{selectedProjectForChat.name}</span>
                <span className="text-xs bg-moss-primary/10 text-moss-primary px-2 py-0.5 rounded-full">
                  {selectedProjectForChat.metadata1}
                </span>
              </div>
              <div className="flex items-center gap-4 text-sm text-neutral-600">
                <span>📅 {selectedProjectForChat.year}</span>
                <span>💰 {formatCurrency(selectedProjectForChat.budget || 0)}</span>
              </div>
            </div>

            <div className="bg-neutral-50 rounded-xl p-4 mb-4">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-brick-primary/20 rounded-full flex items-center justify-center shrink-0">
                  <ChatCircle size={16} className="text-brick-primary" />
                </div>
                <div className="text-sm text-neutral-700 leading-relaxed">
                  Hallo! Ik ben Ella, je AI-assistent. Ik ga je straks alles vertellen over dit renovatieproject - 
                  van de planning en voortgang tot de financiële details en betrokken leveranciers. 
                  <br /><br />
                  <em className="text-neutral-500">
                    (De volledige projectuitleg functionaliteit wordt binnenkort toegevoegd...)
                  </em>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-end gap-3">
              <button
                onClick={() => setEllaChatboxOpen(false)}
                className="px-4 py-2 rounded-lg border border-neutral-200 text-neutral-600 hover:bg-neutral-50 transition-colors"
              >
                Sluiten
              </button>
              <button
                onClick={() => {
                  // TODO: Implement full Ella explanation functionality
                  console.log('Starting Ella explanation for project:', selectedProjectForChat.name);
                }}
                className="px-4 py-2 rounded-lg bg-brick-primary text-white hover:bg-brick-primary/90 transition-colors"
              >
                Start uitleg
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

DirectSearchComponent.displayName = 'DirectSearchComponent';

export default DirectSearchComponent; 